import React, { useState } from "react";
import { Popconfirm } from "antd";

import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { Floors, QuickformAttributes } from "@api/client";
import { useToggle } from "@hooks/use-toggle";
import { usePermissions } from "@hooks/use-permission";
import { DeleteButton } from "@components/delete-button";

import { HandleAttributeValueUpdate, HandleDeleteFloor, HandleDeleteModule } from "../types";
import * as S from "../quick-form.styled";
import { AddModuleValueForm } from "../add-module-value-form";
import { AttributeFloorValue } from "../attribute-floor-value";
import { CollapsibleTableRowRetail } from "../collapsedTableRows/collapsible-table-row-retail";

interface TableRowProps {
  floor: Floors;
  parentAttributes: QuickformAttributes;
  onAttributeUpdate: HandleAttributeValueUpdate;
  onDeleteFloor: HandleDeleteFloor;
  onDeleteModule: HandleDeleteModule;
}

const arrowOutlinedStyles = { cursor: "pointer", marginRight: "5px", fontSize: "10px", alignSelf: "center" };

export const TableRowRetail = ({
  floor,
  parentAttributes,
  onAttributeUpdate,
  onDeleteFloor,
  onDeleteModule,
}: TableRowProps) => {
  const [showCollapsed, setShowCollapsed] = useState(false);
  const { state: showFloorPopconfirm, toggle: toggleFloorPopconfirm } = useToggle();
  const { hasPermission } = usePermissions();

  return (
    <>
      <S.TableRow>
        <S.TableCell
          style={{ display: "flex", width: "80px" }}
          onClick={() => setShowCollapsed(!showCollapsed)}
        >
          {showCollapsed ? (
            <UpOutlined style={arrowOutlinedStyles} />
          ) : (
            <DownOutlined style={arrowOutlinedStyles} />
          )}
          <span>{floor.floorsOriginal}</span>
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.unit?.id}
            currentValue={floor.unit?.valueOriginal}
            parentAttributeId={parentAttributes.unit?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.unit?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.brand?.id}
            currentValue={floor.brand?.valueOriginal}
            parentAttributeId={parentAttributes.brand?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.brand?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.category1?.id}
            currentValue={floor.category1?.valueOriginal}
            parentAttributeId={parentAttributes.category1?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.category1?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.category2?.id}
            currentValue={floor.category2?.valueOriginal}
            parentAttributeId={parentAttributes.category2?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.category2?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.gLA?.id}
            currentValue={floor.gLA?.valueOriginal}
            parentAttributeId={parentAttributes.gLA?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.gLA?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.salesArea?.id}
            currentValue={floor.salesArea?.valueOriginal}
            parentAttributeId={parentAttributes.salesArea?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.salesArea?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.rent?.id}
            currentValue={floor.rent?.valueOriginal}
            parentAttributeId={parentAttributes.rent?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.rent?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.date?.id}
            currentValue={floor.date?.valueOriginal}
            parentAttributeId={parentAttributes.date?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.date?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.level?.id}
            currentValue={floor.level?.valueOriginal}
            parentAttributeId={parentAttributes.level?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.level?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.notes?.id}
            currentValue={floor.notes?.valueOriginal}
            parentAttributeId={parentAttributes.notes?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.notes?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <Popconfirm
            title="Are you sure that you want to delete this floor?"
            visible={showFloorPopconfirm}
            onConfirm={() => {
              onDeleteFloor(floor.floorsOriginal);
              toggleFloorPopconfirm();
            }}
            onCancel={toggleFloorPopconfirm}
          >
            <DeleteButton onClick={toggleFloorPopconfirm} disabled={!hasPermission("DELETE_ATTRIBUTE")} />
          </Popconfirm>
        </S.TableCell>
      </S.TableRow>
      {showCollapsed &&
        floor.modules &&
        floor.modules.map((module) => {
          return (
            <CollapsibleTableRowRetail
              module={module}
              floor={floor}
              onDeleteModule={onDeleteModule}
              onAttributeUpdate={onAttributeUpdate}
            />
          );
        })}
      {showCollapsed && (
        // avoid stretching rows after list is collapsed
        <div style={{ height: "70px" }}>
          <AddModuleValueForm floorsOriginal={floor.floorsOriginal} />
        </div>
      )}
    </>
  );
};
